import React, {
  useCallback,
  useEffect,
  useState,
} from 'react';
import { Button } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';
import { DateTime } from 'luxon';

import TitleRow from './TitleRow';

import SimpleTextInputModal from '../../../common/modals/SimpleTextInputModal';
import SignatureModal from '../../SignatureModal';
import { getIdMap, uuid } from '../../../helpers/helpers';

import FieldTriggerFlag from './FieldTriggerFlag';
import DisplayText from '../../../common/text/DisplayText';
import SignatureTileView from '../../../common/signatures/SignatureTileView';
import { generateId } from '../../formHelpers';

export default function MultiSignaturePreview({
  configProps = {},
  previewProps = {},
  id,
  responses,
  setResponses,
  responding = false,
  sections,
  projectId,
  templateId,
  divisions,
  isDisplay,
  showCondensedView,
  onFieldTrigger,
  isFormBuilder,
}) {
  const isFieldDisplay = !isFormBuilder && isDisplay;

  const [selectedUser, setSelectedUser] = useState();
  const [oldUsers, setOldUsers] = useState(new Set());
  const [showTextModal, setShowTextModal] = useState(false);

  const {
    optional,
    title = 'Title goes here',
    linkedField,
    fieldTrigger,
    showSignatureTime,
    collectGeolocation,
  } = configProps;
  const {
    values: displayValues,
  } = previewProps;
  const {
    [id]: {
      values: responseValues,
    } = {},
    [linkedField]: {
      values: linkedValues,
    } = {},
  } = responses ?? {};
  const realValues = responding ? responseValues : displayValues;

  const hideSigModal = useCallback(() => setSelectedUser(), []);
  const openSigModal = useCallback((record) => {
    setSelectedUser(record);
  }, []);

  const openTextModal = useCallback(() => setShowTextModal(true), []);
  const closeTextModal = useCallback(() => setShowTextModal(false), []);

  const onAddCustom = useCallback((newSigner) => {
    if (responding && newSigner) {
      const newValue = {
        id: generateId(),
        name: newSigner,
        isCustom: true,
      };
      setResponses((oldResponses = {}) => ({
        ...oldResponses,
        [id]: {
          values: (realValues ?? []).concat([newValue]),
        },
      }));
      setSelectedUser(newValue);
    }
    setShowTextModal(false);
  }, [id, realValues, responding]);

  const onDelete = useCallback((recordId) => {
    const {
      [id]: {
        values: oldResponses = [],
      } = {},
    } = responses;
    const newResponses = oldResponses.filter((res) => res.id !== recordId);
    setResponses({
      ...responses,
      [id]: { values: newResponses },
    });
  }, [id, responses]);

  const onClear = useCallback((recordId) => {
    const {
      [id]: {
        values: oldResponses = [],
      } = {},
    } = responses;
    const newResponses = oldResponses.map((res) => {
      if (res.id !== recordId) return res;
      const newRes = { ...res };
      delete newRes.sig;
      return newRes;
    });
    setResponses({
      ...responses,
      [id]: { values: newResponses },
    });
  }, [id, responses]);

  const onSubmit = useCallback((signatureFile) => {
    if (!selectedUser) return;
    const {
      [id]: {
        values: oldResponses = [],
      } = {},
    } = responses;
    const newResponses = oldResponses.map((res) => {
      if (res.id !== selectedUser.id) return res;
      return {
        ...res,
        sig: signatureFile,
        autoSaveId: uuid(),
        timestamp: generateId(),
        timezone: DateTime.local().zoneName,
      };
    });
    setResponses({
      ...responses,
      [id]: { values: newResponses },
    });
    setSelectedUser();
  }, [id, responses, selectedUser]);

  useEffect(() => {
    /*
      If users in linked field are updated we need to update response
    */
    const safeResponseValues = responseValues ?? [];
    const responseMap = getIdMap(safeResponseValues);
    const safeValues = linkedValues ?? [];
    const newUsers = new Set(safeValues.map((user) => user.id));
    const addedUsers = safeValues.filter((user) => (
      !oldUsers.has(user.id) && !(user.id in responseMap)
    ));
    const deletedUsers = new Set(Array.from(oldUsers).filter((userId) => !newUsers.has(userId)));
    if (addedUsers.length === 0 && deletedUsers.size === 0) return;

    const fullValues = safeResponseValues
      .filter((val) => val.isCustom || !deletedUsers.has(val.id))
      .concat(
        addedUsers.map((user) => {
          const { id: userId } = user;
          const { [userId]: userResponse = {} } = responseMap;
          return {
            ...user,
            ...userResponse,
          };
        }),
      );

    setOldUsers(newUsers);
    setResponses({
      ...responses,
      [id]: {
        values: fullValues,
      },
    });
  }, [id, linkedValues, responses, responseValues, oldUsers]);

  return (
    <div style={{ minWidth: 800, width: '100%' }}>
      <TitleRow
        title={title}
        optional={optional}
        filter={
          fieldTrigger && !isDisplay
            ? (
              <FieldTriggerFlag
                sections={sections}
                templateId={templateId}
                projectId={projectId}
                divisions={divisions}
                configProps={configProps}
                responding={responding}
                onFieldTrigger={onFieldTrigger}
              />
            ) : null
        }
        isFieldDisplay={isFieldDisplay}
      />
      <div
        style={{
          pointerEvents: 'all',
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'wrap',
          gap: 10,
          alignItems: 'flex-start',
          width: '100%',
          marginTop: showCondensedView ? 0 : 15,
        }}
      >
        {!showCondensedView || realValues?.length ? (
          <SignatureTileView
            signatures={realValues ?? []}
            isDisplay={isDisplay}
            onClick={openSigModal}
            onDelete={onDelete}
            onClear={onClear}
            showSignatureTime={showSignatureTime}
            collectGeolocation={collectGeolocation}
          />
        ) : (
          <DisplayText title="No Signatures" style={{ marginBottom: 0 }} />
        )}
        {!isDisplay && (
          <Button
            className="form-attachment-add-button-redesign"
            onClick={openTextModal}
            style={{ height: 155.4, width: 197.5, alignSelf: 'center' }}
          >
            <div className="form-attachment-add-button-center">
              <div className="file-tile-download-icon">
                <PlusOutlined />
              </div>
              <span>
                Add signature
              </span>
            </div>
          </Button>
        )}
      </div>
      <SignatureModal
        visible={selectedUser}
        onClose={hideSigModal}
        onSubmit={onSubmit}
        initialData={selectedUser ? selectedUser.sig : null}
        isMultiSignature
      />
      <SimpleTextInputModal
        visible={showTextModal}
        onClose={closeTextModal}
        onSave={onAddCustom}
        placeholder="Enter user"
        useRedesign
        saveText="Add & sign"
        textLabel={(
          <span className="form-signature-signer-label">Signer</span>
        )}
      />
    </div>
  );
}

/* eslint-disable react/forbid-prop-types */
MultiSignaturePreview.propTypes = {
  configProps: PropTypes.object,
  previewProps: PropTypes.object,
  id: PropTypes.string,
  responses: PropTypes.object,
  setResponses: PropTypes.func,
  responding: PropTypes.bool,
  sections: PropTypes.array,
  projectId: PropTypes.string,
  templateId: PropTypes.string,
  divisions: PropTypes.array,
  isDisplay: PropTypes.bool,
  showCondensedView: PropTypes.bool,
  onFieldTrigger: PropTypes.func,
  isFormBuilder: PropTypes.bool,
};

MultiSignaturePreview.defaultProps = {
  configProps: {},
  previewProps: {},
  id: null,
  responses: null,
  setResponses: null,
  responding: false,
  sections: [],
  projectId: null,
  templateId: null,
  divisions: [],
  isDisplay: false,
  showCondensedView: false,
  onFieldTrigger: null,
  isFormBuilder: false,
};
